import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import { kycStatusQueryKey } from './useKyc';

/** Backend emits this on the user's room when a Sumsub webhook moves identity. */
const KYC_STATUS_CHANGED = 'kyc.status_changed';
/** Distinct event for action verifications (admin trigger / action webhook). */
const KYC_ACTION_STATUS_CHANGED = 'kyc.action_status_changed';

/**
 * Invalidate the KYC status query whenever the backend relays a status change
 * (path A — webhook → Socket.IO `user:<id>` room). Covers both identity moves
 * and action-verification moves; the latter is what lets an admin-triggered
 * action pop the action gate live (a refreshed `/kyc/status` re-runs
 * `useKycActionAutoGate`) without the player reloading. This keeps `/kyc/status`
 * fresh even when the user isn't inside the WebSDK (the SDK's own onMessage
 * handles the in-popup case). Mount once near the root, inside
 * QueryClientProvider.
 */
export function useKycRealtime(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const socket = getSocket();
    const onChange = () => {
      void queryClient.invalidateQueries({ queryKey: kycStatusQueryKey() });
    };
    socket.on(KYC_STATUS_CHANGED, onChange);
    socket.on(KYC_ACTION_STATUS_CHANGED, onChange);
    return () => {
      socket.off(KYC_STATUS_CHANGED, onChange);
      socket.off(KYC_ACTION_STATUS_CHANGED, onChange);
    };
  }, [queryClient]);
}
